/**
 * Subject-line generation helpers — extracted from NewsletterAIService.js
 * as part of #229 Wave 3 (the decomposition sequence).
 *
 * The class methods this module backs are 1-line delegates that pass the
 * service instance as the first argument so the function can reuse the
 * class's apiEndpoint / logger / auth / budget plumbing without dragging
 * that state into the module.
 *
 * Public exports:
 *   - generateSubjectLines(service, params) — service-bound helper that
 *     prompts the LLM for N compliant subject-line candidates given a
 *     newsletter body. Filters output by length cap (≤ 60 chars) and the
 *     SEC/FINRA banned-phrase list (see isSubjectLineCompliant).
 *   - parseSubjectLineResponse(rawContent) — pure parser. Accepts JSON
 *     array, JSON object with `subjects`, newline-separated text, or
 *     bullet/numbered lists. Returns a string[].
 *   - isSubjectLineCompliant(subject) — pure SEC/FINRA banned-phrase
 *     filter. Returns true if the subject contains none of the banned
 *     superlatives or performance guarantees.
 */

import aiUsageStatsService from '../AIUsageStatsService.js';
import {
  createNetworkError,
  createValidationError,
} from '../../utils/StandardErrorFormat.js';

/**
 * Parse the LLM response into a string array of candidate subjects.
 * Pure function — no service deps.
 */
export function parseSubjectLineResponse(rawContent) {
  if (!rawContent) return [];

  // Strip markdown code fences if present
  const cleaned = String(rawContent)
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();

  // Try JSON first
  try {
    const parsed = JSON.parse(cleaned);
    if (Array.isArray(parsed)) {
      return parsed.filter((s) => typeof s === 'string');
    }
    if (parsed && Array.isArray(parsed.subjects)) {
      return parsed.subjects.filter((s) => typeof s === 'string');
    }
  } catch {
    // fall through to line-based parsing
  }

  // Fall back to line-based parsing (strip bullets / numbering)
  return cleaned
    .split('\n')
    .map((line) =>
      line
        .replace(/^\s*[-*•]\s+/, '')
        .replace(/^\s*\d+[.)]\s+/, '')
        .replace(/^["']|["']$/g, '')
        .trim()
    )
    .filter((line) => line.length > 0);
}

/**
 * SEC/FINRA banned-phrase filter for subject lines. Pure function.
 * Returns true if the subject contains NONE of the banned phrases.
 */
export function isSubjectLineCompliant(subject) {
  if (!subject || typeof subject !== 'string') return false;
  const lower = subject.toLowerCase();
  const bannedPhrases = [
    'guaranteed',
    'risk-free',
    'risk free',
    'insider tip',
    'insider tips',
    'guaranteed returns',
    'no risk',
    'sure thing',
    'cant lose',
    "can't lose"
  ];
  return !bannedPhrases.some((phrase) => lower.includes(phrase));
}

/**
 * Generate N compliant subject-line candidates for the given newsletter
 * body. Service-bound: uses the caller's apiEndpoint + auth + logger +
 * budget plumbing.
 *
 * @param {Object} service - NewsletterAIService instance
 * @param {Object} params
 * @param {string} params.content - Newsletter body (plain text or HTML)
 * @param {string} [params.tone='professional'] - Tone for the subjects
 * @param {number} [params.count=3] - Number of candidates to return (1..10)
 * @param {boolean} [params.useStyleProfile=true] - Inject advisor voice block
 * @returns {Promise<string[]>} Compliant subject-line candidates (≤ count items)
 */
export async function generateSubjectLines(
  service,
  { content, tone = 'professional', count = 3, useStyleProfile = true } = {}
) {
  await service._assertWithinDailyBudget();
  // Validate content (must be non-empty after trim)
  if (!content || typeof content !== 'string' || content.trim().length === 0) {
    throw createValidationError(
      'Content is required to generate subject line suggestions. Add newsletter content first.'
    );
  }

  await service.initialize();

  // Build a tightly-scoped prompt. We keep this small and cheap.
  const safeTone = (tone || 'professional').toString().trim() || 'professional';
  const requestedCount = Math.max(1, Math.min(10, parseInt(count, 10) || 3));

  // Truncate body to keep prompt cost low (~ first 2000 chars is plenty for a subject)
  const bodyExcerpt = content.trim().slice(0, 2000);

  // Sprint 5 #106 — inject the advisor's saved 9-dim voice block so
  // subject lines match their voice. Caller can opt out with
  // useStyleProfile: false (e.g. for compliance-only flows).
  let voiceBlock = '';
  if (useStyleProfile) {
    const communicationStyle = await service._getAdvisorCommunicationStyle();
    voiceBlock = service._buildCommunicationStyleBlock(communicationStyle);
    service.logger.info('🎨 [STYLE] CommunicationStyle injected into subject-line prompt', {
      source: communicationStyle ? 'advisor_profile' : 'system_default'
    });
  }

  // Written for current Claude models: constraints once, at normal volume,
  // with the reason. Promissory terms are described rather than named so the
  // prompt itself stays clean when compliance scanners read it.
  const guardrails =
    'You are an expert financial newsletter editor writing email subject lines for a registered investment advisor. ' +
    `Generate exactly ${requestedCount} compelling, professional subject line candidates based on the newsletter body below. ` +
    `Match this tone: ${safeTone}. ` +
    'Keep each subject line to 60 characters or fewer so it survives mobile inbox truncation. ' +
    'Use measured language consistent with SEC and FINRA marketing rules, with no promissory or superlative performance claims. ' +
    'Be specific to the newsletter body rather than clickbait. ' +
    'Return a valid JSON array of strings and nothing else, with no commentary or markdown.';

  // Voice block first (sets baseline voice), then guardrails (override on
  // anything that conflicts — compliance always wins).
  const systemPrompt = voiceBlock ? `${voiceBlock}\n\n${guardrails}` : guardrails;

  const userPrompt = `NEWSLETTER BODY:\n${bodyExcerpt}\n\nReturn ${requestedCount} subject line candidates as a JSON array of strings.`;

  const requestBody = {
    prompt: userPrompt,
    systemPrompt,
    tone: safeTone,
    mode: 'subject-lines',
    maxTokens: 300
  };

  let response;
  try {
    const token = await service.getAuthToken();
    response = await fetch(service.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token
      },
      body: JSON.stringify(requestBody)
    });
  } catch (error) {
    service.logger.error('Subject line generation request failed:', error);
    if (error.name === 'ServiceError') throw error;
    createNetworkError('Subject line generation failed', error);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    createNetworkError(
      errorData.error || `Subject line generation failed with status ${response.status}`
    );
  }

  const data = await response.json();
  const rawContent = data.content || data.text || '';

  // Track AI usage with the 'subject-lines' feature label
  try {
    await aiUsageStatsService.updateStats({
      tokensUsed: data.metadata?.tokensUsed?.total || 0,
      cost: data.metadata?.estimatedCost?.total || 0,
      feature: 'subject-lines',
      provider: data.metadata?.provider || null
    });
  } catch (statsError) {
    service.logger.warn('Subject line usage stats update failed:', statsError);
  }

  // Parse the response — try JSON first, then fall back to line splitting
  const candidates = parseSubjectLineResponse(rawContent);

  // Filter compliance violations and length cap
  const filtered = candidates
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => s.length > 0 && s.length <= 60)
    .filter((s) => isSubjectLineCompliant(s));

  // Return up to requestedCount items
  return filtered.slice(0, requestedCount);
}
